import React, { useState, useEffect } from 'react';
import { useMutation, useQuery, useLazyQuery } from '@apollo/client';
import {
  Box,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
  Alert,
  Stack,
  Avatar,
  Tooltip,
  Fab,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Checkbox,
  Divider,
  Grid,
  InputAdornment,
  Badge
} from '@mui/material';
import {
  Add as AddIcon,
  ShoppingCart as ShoppingCartIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Store as StoreIcon,
  PictureAsPdf as PdfIcon,
  TableChart as CsvIcon,
  AutoAwesome as AutoIcon,
  LocalGroceryStore as GroceryIcon,
  Kitchen as KitchenIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import {
  LIST_GROCERY_LISTS,
  GET_GROCERY_LIST,
  GET_ACTIVE_MEAL_PLAN,
  EXPORT_GROCERY_LIST
} from '../graphql/queries';
import {
  GENERATE_GROCERY_LIST,
  UPDATE_GROCERY_ITEM,
  ADD_GROCERY_ITEM,
  REMOVE_GROCERY_ITEM
} from '../graphql/mutations';
import { ON_GROCERY_LIST_UPDATE } from '../graphql/subscriptions';

interface GroceryItem {
  id: string;
  name: string;
  quantity: number;
  unit: string;
  category: string;
  aisle: string;
  checked: boolean;
  recipeIds: string[];
  notes: string;
}

interface GroceryList {
  householdId: string;
  listId: string;
  planId?: string;
  items: GroceryItem[];
  instacartLinkUrl?: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

const GroceryLists: React.FC = () => {
  const [householdId] = useState('default-household');
  const [selectedList, setSelectedList] = useState<GroceryList | null>(null);
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [addItemDialogOpen, setAddItemDialogOpen] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [newItem, setNewItem] = useState({
    name: '',
    quantity: 1,
    unit: 'unit',
    category: 'other',
    notes: ''
  });
  const [groupByAisle, setGroupByAisle] = useState(true);

  // Queries - subscription temporarily disabled due to WebSocket issues
  const { data: listsData, loading: listsLoading, refetch: refetchLists } = useQuery(LIST_GROCERY_LISTS, {
    variables: { householdId, limit: 10 },
    skip: !householdId,
    // Poll every 5 seconds instead of using subscriptions
    pollInterval: 5000
  });

  const { data: activePlanData } = useQuery(GET_ACTIVE_MEAL_PLAN, {
    variables: { householdId },
    skip: !householdId
  });

  const [getGroceryList, { data: listData, loading: listLoading }] = useLazyQuery(GET_GROCERY_LIST);

  const [exportGroceryList] = useLazyQuery(EXPORT_GROCERY_LIST);

  // Mutations
  const [generateGroceryList, { loading: generating }] = useMutation(GENERATE_GROCERY_LIST, {
    onCompleted: (data) => {
      setGenerateDialogOpen(false);
      refetchLists();
      setSelectedList(data.generateGroceryList);
    }
  });

  const [updateGroceryItem] = useMutation(UPDATE_GROCERY_ITEM, {
    onCompleted: (data) => {
      setSelectedList(data.updateGroceryItem);
    }
  });

  const [addGroceryItem] = useMutation(ADD_GROCERY_ITEM, {
    onCompleted: (data) => {
      setSelectedList(data.addGroceryItem);
      setAddItemDialogOpen(false);
      setNewItem({
        name: '',
        quantity: 1,
        unit: 'unit',
        category: 'other',
        notes: ''
      });
    }
  });

  const [removeGroceryItem] = useMutation(REMOVE_GROCERY_ITEM, {
    onCompleted: (data) => {
      setSelectedList(data.removeGroceryItem);
    }
  });

  // Subscribe to real-time updates for grocery lists
  // TEMPORARILY DISABLED: WebSocket connection issues with AppSync
  // The page will still work, just without real-time updates
  // Users can manually refresh to see updates
  /*
  useEffect(() => {
    if (!householdId || !subscribeToMore) return;

    const unsubscribe = subscribeToMore({
      document: ON_GROCERY_LIST_UPDATE,
      variables: { householdId },
      updateQuery: (prev, { subscriptionData }) => {
        if (!subscriptionData.data) return prev;
        
        const updatedList = subscriptionData.data.onGroceryListUpdate;
        
        // Update the selected list if it matches
        if (selectedList?.listId === updatedList.listId) {
          setSelectedList(updatedList);
        }
        
        // Update the lists in the sidebar
        const existingLists = prev.listGroceryLists || [];
        const listIndex = existingLists.findIndex(
          (list: GroceryList) => list.listId === updatedList.listId
        );
        
        if (listIndex >= 0) {
          // Update existing list
          const newLists = [...existingLists];
          newLists[listIndex] = updatedList;
          return {
            ...prev,
            listGroceryLists: newLists
          };
        } else {
          // Add new list
          return {
            ...prev,
            listGroceryLists: [updatedList, ...existingLists]
          };
        }
      },
      onError: (error) => {
        console.error('Subscription error:', error);
      }
    });

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [householdId, subscribeToMore, selectedList?.listId]);
  */

  // Load selected list details when a list is selected
  useEffect(() => {
    if (selectedList?.listId && !selectedList.items) {
      getGroceryList({
        variables: {
          householdId,
          listId: selectedList.listId
        }
      });
    }
  }, [selectedList, getGroceryList, householdId]);

  useEffect(() => {
    if (listData?.getGroceryList) {
      setSelectedList(listData.getGroceryList);
    }
  }, [listData]);

  const handleGenerateFromMealPlan = () => {
    if (activePlanData?.getActiveMealPlan) {
      generateGroceryList({
        variables: {
          householdId,
          input: {
            planId: activePlanData.getActiveMealPlan.planId
          }
        }
      });
    }
  };

  const handleGenerateEmpty = () => {
    generateGroceryList({
      variables: {
        householdId,
        input: {
          additionalItems: []
        }
      }
    });
  };

  const handleToggleItem = (itemId: string, checked: boolean) => {
    if (selectedList) {
      updateGroceryItem({
        variables: {
          householdId,
          listId: selectedList.listId,
          itemId,
          checked
        }
      });
    }
  };

  const handleAddItem = () => {
    if (selectedList && newItem.name) {
      addGroceryItem({
        variables: {
          householdId,
          listId: selectedList.listId,
          input: newItem
        }
      });
    }
  };

  const handleRemoveItem = (itemId: string) => {
    if (selectedList) {
      removeGroceryItem({
        variables: {
          householdId,
          listId: selectedList.listId,
          itemId
        }
      });
    }
  };

  const handleExport = async (format: 'PDF' | 'CSV') => {
    if (selectedList) {
      const { data } = await exportGroceryList({
        variables: {
          householdId,
          listId: selectedList.listId,
          format
        }
      });
      
      if (data?.exportGroceryList) {
        // Decode base64 and download
        const base64Data = data.exportGroceryList;
        const byteCharacters = atob(base64Data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        
        if (format === 'PDF') {
          // For PDF format, we actually get HTML that can be printed as PDF
          const htmlContent = new TextDecoder().decode(byteArray);
          const printWindow = window.open('', '_blank');
          if (printWindow) {
            printWindow.document.write(htmlContent);
            printWindow.document.close();
            printWindow.focus();
            // Auto-trigger print dialog which allows saving as PDF
            setTimeout(() => {
              printWindow.print();
            }, 500);
          }
        } else {
          // CSV format works as before
          const blob = new Blob([byteArray], { type: 'text/csv' });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `grocery-list.csv`;
          a.click();
          window.URL.revokeObjectURL(url);
        }
      }
    }
    setExportDialogOpen(false);
  };

  const getItemsByAisle = () => {
    if (!selectedList?.items) return {};
    
    const grouped: Record<string, GroceryItem[]> = {};
    selectedList.items.forEach(item => {
      const aisle = item.aisle || 'Various';
      if (!grouped[aisle]) {
        grouped[aisle] = [];
      }
      grouped[aisle].push(item);
    });
    
    return grouped;
  };

  const checkedCount = selectedList?.items?.filter(item => item.checked).length || 0;
  const totalCount = selectedList?.items?.length || 0;
  const progress = totalCount > 0 ? (checkedCount / totalCount) * 100 : 0;

  const ListSidebar = () => (
    <Paper sx={{ p: 2, height: '100%' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6" fontWeight="bold">
          Your Lists
        </Typography>
        <IconButton onClick={() => refetchLists()} size="small">
          <RefreshIcon />
        </IconButton>
      </Box>
      
      {listsLoading ? (
        <LinearProgress />
      ) : (
        <List>
          {listsData?.listGroceryLists?.map((list: GroceryList) => {
            const isSelected = selectedList?.listId === list.listId;
            const listDate = new Date(list.createdAt);
            
            return (
              <ListItemButton
                key={list.listId}
                selected={isSelected}
                onClick={() => setSelectedList(list)}
                sx={{
                  borderRadius: 1,
                  mb: 1,
                  bgcolor: isSelected ? 'action.selected' : 'transparent'
                }}
              >
                <ListItemIcon>
                  <Avatar sx={{ bgcolor: isSelected ? 'primary.main' : 'grey.400' }}>
                    <ShoppingCartIcon />
                  </Avatar>
                </ListItemIcon>
                <ListItemText
                  primary={format(listDate, 'MMM d, yyyy')}
                  secondary={`${list.items?.length || 0} items`}
                />
                {list.status === 'active' && (
                  <Chip label="Active" size="small" color="success" />
                )}
              </ListItemButton>
            );
          })}
        </List>
      )}
      
      <Divider sx={{ my: 2 }} />
      
      <Stack spacing={2}>
        <Button
          fullWidth
          variant="contained"
          startIcon={<AutoIcon />}
          onClick={() => setGenerateDialogOpen(true)}
          disabled={!activePlanData?.getActiveMealPlan && !listsData?.listGroceryLists}
          sx={{
            background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
            color: 'white'
          }}
        >
          Generate New List
        </Button>
      </Stack>
    </Paper>
  );

  const ItemsList = () => {
    if (!selectedList) {
      return (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <GroceryIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            No List Selected
          </Typography>
          <Typography variant="body1" color="text.secondary" mb={3}>
            Select a list from the sidebar or generate a new one
          </Typography>
        </Paper>
      );
    }

    const itemsByAisle = groupByAisle ? getItemsByAisle() : { 'All Items': selectedList.items };

    return (
      <Paper sx={{ p: 2, height: '100%', overflow: 'auto' }}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box>
            <Typography variant="h5" fontWeight="bold">
              Shopping List
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {format(new Date(selectedList.createdAt), 'MMMM d, yyyy')}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Tooltip title="Add Item">
              <IconButton onClick={() => setAddItemDialogOpen(true)}>
                <AddIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Export">
              <IconButton onClick={() => setExportDialogOpen(true)}>
                <DownloadIcon />
              </IconButton>
            </Tooltip>
            {selectedList.instacartLinkUrl && (
              <Tooltip title="Open in Instacart">
                <IconButton onClick={() => window.open(selectedList.instacartLinkUrl, '_blank')}>
                  <StoreIcon />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Box>

        {/* Progress Bar */}
        <Box mb={3}>
          <Box display="flex" justifyContent="space-between" mb={1}>
            <Typography variant="body2">
              Progress: {checkedCount} of {totalCount} items
            </Typography>
            <Typography variant="body2" fontWeight="bold">
              {Math.round(progress)}%
            </Typography>
          </Box>
          <LinearProgress variant="determinate" value={progress} sx={{ height: 8, borderRadius: 1 }} />
        </Box>

        {/* Toggle Group By */}
        <Box mb={2}>
          <Button
            size="small"
            variant={groupByAisle ? 'contained' : 'outlined'}
            onClick={() => setGroupByAisle(!groupByAisle)}
          >
            {groupByAisle ? 'Grouped by Aisle' : 'Show All Items'}
          </Button>
        </Box>

        {/* Items List */}
        {listLoading ? (
          <LinearProgress />
        ) : (
          Object.entries(itemsByAisle).map(([aisle, items]) => (
            <Box key={aisle} mb={3}>
              {groupByAisle && (
                <Typography variant="subtitle1" fontWeight="bold" color="primary" mb={1}>
                  {aisle}
                </Typography>
              )}
              <List>
                {items.map((item) => (
                  <ListItem
                    key={item.id}
                    sx={{
                      bgcolor: item.checked ? 'action.hover' : 'transparent',
                      borderRadius: 1,
                      mb: 0.5,
                      opacity: item.checked ? 0.6 : 1
                    }}
                  >
                    <ListItemIcon>
                      <Checkbox
                        checked={item.checked}
                        onChange={(e) => handleToggleItem(item.id, e.target.checked)}
                        color="primary"
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography
                          sx={{
                            textDecoration: item.checked ? 'line-through' : 'none'
                          }}
                        >
                          {item.name}
                        </Typography>
                      }
                      secondary={
                        <Box display="flex" gap={1} alignItems="center">
                          <Chip
                            label={`${item.quantity} ${item.unit}`}
                            size="small"
                            variant="outlined"
                          />
                          {item.category && (
                            <Chip
                              label={item.category}
                              size="small"
                              variant="outlined"
                              color="secondary"
                            />
                          )}
                          {item.notes && (
                            <Typography variant="caption" color="text.secondary">
                              {item.notes}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        onClick={() => handleRemoveItem(item.id)}
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </Box>
          ))
        )}
      </Paper>
    );
  };

  return (
    <Box sx={{ p: 3, height: 'calc(100vh - 100px)' }}>
      <Box mb={3}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Grocery Lists
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage your shopping lists with smart organization
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: 3, height: 'calc(100% - 100px)' }}>
        <Box sx={{ flex: '0 0 25%', minWidth: 280 }}>
          <ListSidebar />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <ItemsList />
        </Box>
      </Box>

      {/* Generate List Dialog */}
      <Dialog open={generateDialogOpen} onClose={() => setGenerateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Generate New Grocery List</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            {activePlanData?.getActiveMealPlan ? (
              <Card variant="outlined" sx={{ cursor: 'pointer' }} onClick={handleGenerateFromMealPlan}>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Avatar sx={{ bgcolor: 'primary.main' }}>
                      <AutoIcon />
                    </Avatar>
                    <Box flex={1}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        Generate from Active Meal Plan
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Automatically create a list from your current week's meals
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ) : (
              <Alert severity="info">
                No active meal plan found. Create a meal plan first to generate a list from it.
              </Alert>
            )}
            
            <Card variant="outlined" sx={{ cursor: 'pointer' }} onClick={handleGenerateEmpty}>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2}>
                  <Avatar sx={{ bgcolor: 'grey.500' }}>
                    <AddIcon />
                  </Avatar>
                  <Box flex={1}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      Create Empty List
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Start with a blank list and add items manually
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGenerateDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Add Item Dialog */}
      <Dialog open={addItemDialogOpen} onClose={() => setAddItemDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Item to List</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <TextField
              label="Item Name"
              value={newItem.name}
              onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
              fullWidth
              required
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Quantity"
                type="number"
                value={newItem.quantity}
                onChange={(e) => setNewItem({ ...newItem, quantity: parseInt(e.target.value) || 1 })}
                fullWidth
              />
              <TextField
                label="Unit"
                value={newItem.unit}
                onChange={(e) => setNewItem({ ...newItem, unit: e.target.value })}
                fullWidth
              />
            </Box>
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={newItem.category}
                onChange={(e) => setNewItem({ ...newItem, category: e.target.value })}
              >
                <MenuItem value="produce">Produce</MenuItem>
                <MenuItem value="dairy">Dairy</MenuItem>
                <MenuItem value="meat">Meat</MenuItem>
                <MenuItem value="bakery">Bakery</MenuItem>
                <MenuItem value="pantry">Pantry</MenuItem>
                <MenuItem value="frozen">Frozen</MenuItem>
                <MenuItem value="beverages">Beverages</MenuItem>
                <MenuItem value="snacks">Snacks</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Notes"
              value={newItem.notes}
              onChange={(e) => setNewItem({ ...newItem, notes: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddItemDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAddItem} variant="contained" disabled={!newItem.name}>
            Add Item
          </Button>
        </DialogActions>
      </Dialog>

      {/* Export Dialog */}
      <Dialog open={exportDialogOpen} onClose={() => setExportDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Export Grocery List</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <Card variant="outlined" sx={{ cursor: 'pointer' }} onClick={() => handleExport('PDF')}>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2}>
                  <Avatar sx={{ bgcolor: 'error.main' }}>
                    <PdfIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold">
                      Export as PDF
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Formatted for printing and sharing
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
            <Card variant="outlined" sx={{ cursor: 'pointer' }} onClick={() => handleExport('CSV')}>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2}>
                  <Avatar sx={{ bgcolor: 'success.main' }}>
                    <CsvIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold">
                      Export as CSV
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      For spreadsheet applications
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GroceryLists;
